import React from 'react';
import { motion } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { apiService, getCurrentLocale } from '../services/api';
import { getBestImageUrl } from '../utils/imageUrl';

interface StrapiImageFormat {
  url: string;
  width: number;
  height: number;
  size: number;
  ext: string;
  mime: string;
  hash: string;
  name: string;
  path: string | null;
  sizeInBytes: number;
}

interface AboutSectionData {
  id: number;
  documentId: string;
  title: string;
  content: string;
  image?: {
    id: number;
    url: string;
    alternativeText?: string;
    formats?: {
      large?: StrapiImageFormat;
      medium?: StrapiImageFormat;
      small?: StrapiImageFormat;
      thumbnail?: StrapiImageFormat;
    };
    width?: number;
    height?: number;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

function AboutSection(): React.JSX.Element {
  const { data, loading, error } = useApi(apiService.getAboutSectionContent);
  
  // Cast data to proper type
  const aboutData = data as AboutSectionData | null;

  // Language detection helper
  const hasArabic = (text: string | undefined | null): boolean => {
    if (!text) return false;
    return /[\u0600-\u06FF]/.test(text);
  };
  
  // Debug logging for about section data
  if (aboutData && !loading && !error) {
    console.log('📝 AboutSection - ✅ Strapi data loaded:', !!aboutData);
    console.log('📝 AboutSection - Data keys:', Object.keys(aboutData || {}));
    console.log('📝 AboutSection - Title:', aboutData.title);
    console.log('📝 AboutSection - Content length:', aboutData.content?.length || 0);
    console.log('📝 AboutSection - Has image:', !!aboutData.image);
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  if (loading) {
    return (
      <section className="about section-padding" id="over-ons">
        <div className="about-content">
          <div className="about-text">
            <div className="loading-placeholder">
              <div className="loading-title"></div>
              <div className="loading-text"></div>
            </div>
          </div>
          <div className="about-image">
            <div className="loading-placeholder">Loading image...</div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="about section-padding" id="over-ons">
        <div className="about-content">
          <div className="about-text">
            <h2>Over HairMedics</h2>
            <p>
              Ons team van ervaren specialisten maakt gebruik van de nieuwste technieken en behandelmethoden
              om jou de best mogelijke zorg te bieden. Persoonlijke aandacht en een behandelplan op maat staan
              bij ons centraal.
            </p>
          </div>
          <div className="about-image">
            <div className="loading-placeholder">Loading image...</div>
          </div>
        </div>
      </section>
    );
  }

  // Locale-aware fallbacks to avoid wrong-language content rendering
  const currentLocale = getCurrentLocale();

  const rawTitle = aboutData?.title;
  const rawContent = (aboutData?.content || aboutData?.description) || '';

  const DEFAULT_NL_TITLE = 'Over HairMedics';
  const DEFAULT_NL_CONTENT = 'Ons team van ervaren specialisten maakt gebruik van de nieuwste technieken en behandelmethoden om jou de best mogelijke zorg te bieden. Persoonlijke aandacht en een behandelplan op maat staan bij ons centraal.';
  const DEFAULT_AR_TITLE = 'العناية المناسبة لشعرك';
  const DEFAULT_AR_CONTENT = 'تساقط الشعر، ترقق الشعر، أو فروة رأس متقلبة أكثر شيوعًا مما تعتقد، وغالبًا ما يبدأ في سن مبكرة. كثيرون ينتظرون طويلًا، خجلًا أو قلقًا. لكن كلما بدأت مبكرًا، كانت النتيجة أفضل. مع أكثر من 30 عامًا من الخبرة، نساعدك في التشخيص الاحترافي والعلاج المثبت.';

  let title = rawTitle || (currentLocale === 'ar' ? DEFAULT_AR_TITLE : DEFAULT_NL_TITLE);
  let content = rawContent || (currentLocale === 'ar' ? DEFAULT_AR_CONTENT : DEFAULT_NL_CONTENT);

  // If locale is NL but text is Arabic, fallback to NL defaults; vice versa for AR
  if (currentLocale === 'nl' && (hasArabic(title) || hasArabic(content))) {
    title = DEFAULT_NL_TITLE;
    content = DEFAULT_NL_CONTENT;
  }
  if (currentLocale === 'ar' && !hasArabic(title) && !hasArabic(content)) {
    title = DEFAULT_AR_TITLE;
    content = DEFAULT_AR_CONTENT;
  }

  // Get best available image URL from Strapi (handle both flat and nested structures)
  const imageSource = (aboutData as any)?.image?.data?.attributes || (aboutData as any)?.image;
  const imageUrl = getBestImageUrl(imageSource as any);

  return (
    <motion.section
      className="about section-padding"
      id="over-ons"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      dir={currentLocale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="about-content">
        <motion.div className="about-text" variants={itemVariants}>
          <h2>{title}</h2>
          <div 
            className="about-text-content" 
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </motion.div>
        <motion.div className="about-image" variants={itemVariants}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={aboutData?.image?.alternativeText || "Over HairMedics"} 
              className="about-img"
            />
          ) : (
            <div className="loading-placeholder">No image available</div>
          )}
        </motion.div>
      </div>
    </motion.section>
  );
}

export default AboutSection;